/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

import { CheckboxInput, Table } from '@coursera/cds-core';
import type { TableColumn, TableProps } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-ui';

type InternalRow<TRow> = TRow & { _selected: boolean; _rowKey: string };
type InternalKey<TRow> = keyof InternalRow<TRow> & string;

type BaseProps<TRow extends Record<string, unknown>> = Omit<
  TableProps<InternalRow<TRow>, InternalKey<TRow>>,
  'rows' | 'columns'
> & {
  /** Column definitions for the table (excluding the prepended selection checkbox column). */
  columns: Array<TableColumn<TRow, keyof TRow & string>>;
  /** Data rows to render. Each row must be a plain object. */
  rows: TRow[];
  /** Returns a stable unique string key for a given row, used to track selection state. */
  getRowKey: (row: TRow) => string;
};

type SelectionProps = {
  /** When true, checkboxes are shown but disabled and the bulk-action bar is hidden. */
  selectionDisabled?: boolean;
  /** Set of row keys that are individually selected (ignored when selectAll is true). */
  selectedRowKeys?: Set<string>;
  /** When true, all rows are considered selected regardless of selectedRowKeys. */
  selectAll?: boolean;
  /**
   * Called whenever selection changes.
   * @param keys - The new set of individually selected row keys.
   * @param selectAll - Whether the "select all" state is active.
   */
  onSelectionChange?: (keys: Set<string>, selectAll: boolean) => void;
  /**
   * Render prop for the bulk-action bar. Rendered as an overlay above the table header
   * whenever at least one row is selected (or selectAll is true) and rows is non-empty.
   * Compose a BulkActionBar here with the action buttons relevant to your context.
   */
  bulkActions?: ReactNode;
};

type Props<TRow extends Record<string, unknown>> = BaseProps<TRow> & SelectionProps;

const styles = {
  container: css`
    display: grid;

    td:first-of-type,
    th:first-of-type {
      min-width: 72px;
      max-width: 72px;
      width: 72px;
    }

    figcaption {
      margin-bottom: 0 !important;
    }

    tbody tr:has(td:first-of-type input[type='checkbox']:checked) td {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
    }
  `,
  bulkActionsLayer: css`
    grid-column: 1;
    grid-row: 1;
    align-self: start;
    z-index: 4;
  `,
  tableLayer: css`
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
  `,
  headerCheckbox: css`
    svg {
      display: block !important;
    }
  `,
  srOnly: css`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
};

/**
 * A data table with built-in row selection and an optional bulk-action bar.
 *
 * Wraps the CDS `Table` and prepends a checkbox column for selecting rows.
 * When rows are selected, `bulkActions` is rendered as an overlay above the
 * table header so consumers can compose contextual actions (e.g. delete, assign).
 *
 * Selection state is fully controlled — the parent owns `selectedRowKeys` and
 * `selectAll` and updates them via `onSelectionChange`. Pass `selectionDisabled`
 * to show checkboxes in a disabled state without triggering selection callbacks
 * (useful during loading or when the user lacks permission).
 *
 * @example
 * ```tsx
 * <ActionTable
 *   aria-label="Groups"
 *   columns={COLUMNS}
 *   rows={rows}
 *   totalRows={total}
 *   getRowKey={(row) => row.id}
 *   selectedRowKeys={selectedKeys}
 *   selectAll={selectAll}
 *   onSelectionChange={(keys, all) => { setSelectedKeys(keys); setSelectAll(all); }}
 *   bulkActions={<BulkActionBar label={`${selectedKeys.size} selected`} ... />}
 * />
 * ```
 */
function ActionTable<TRow extends Record<string, unknown>>({
  columns,
  rows,
  getRowKey,
  selectionDisabled,
  selectedRowKeys,
  selectAll,
  onSelectionChange,
  bulkActions,
  ...tableProps
}: Props<TRow>) {
  const internalRows: Array<InternalRow<TRow>> = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        _selected: !selectionDisabled && (!!selectAll || !!selectedRowKeys?.has(getRowKey(row))),
        _rowKey: getRowKey(row),
      })),
    [rows, selectionDisabled, selectAll, selectedRowKeys, getRowKey]
  );

  const handleHeaderChange = useCallback<() => void>(() => {
    if (selectAll || (selectedRowKeys?.size ?? 0) > 0) {
      onSelectionChange?.(new Set(), false);
    } else {
      onSelectionChange?.(new Set(), true);
    }
  }, [selectAll, selectedRowKeys, onSelectionChange]);

  const handleRowChange = useCallback<(rowKey: string) => void>(
    (rowKey) => {
      const next = new Set(selectedRowKeys);
      if (selectedRowKeys?.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      onSelectionChange?.(next, false);
    },
    [selectedRowKeys, onSelectionChange]
  );

  const checkboxColumn: TableColumn<InternalRow<TRow>, '_selected'> = {
    accessor: '_selected',
    width: '24px',
    // CDS Table renders `label` as ReactNode in the <th> despite the string type.
    // There is no `renderHeader` API, so this cast is the only way to place an
    // interactive checkbox in the column header.
    label: (
      <CheckboxInput
        css={styles.headerCheckbox}
        aria-label={selectAll || (selectedRowKeys?.size ?? 0) > 0 ? _t('Deselect all rows') : _t('Select all rows')}
        checked={!!selectAll}
        indeterminate={!selectAll && (selectedRowKeys?.size ?? 0) > 0}
        disabled={selectionDisabled || rows.length === 0}
        onChange={handleHeaderChange}
      />
    ) as unknown as string,
    renderCell: (row) => (
      <CheckboxInput
        aria-label={row._selected ? _t('Deselect row') : _t('Select row')}
        checked={row._selected}
        disabled={selectionDisabled || !!selectAll}
        onChange={() => handleRowChange(row._rowKey)}
      />
    ),
  };

  const allColumns = [checkboxColumn, ...columns] as unknown as Array<
    TableColumn<InternalRow<TRow>, InternalKey<TRow>>
  >;

  const showBulkActions = !selectionDisabled && rows.length > 0 && (selectAll || (selectedRowKeys?.size ?? 0) > 0);

  const selectedCount = selectedRowKeys?.size ?? 0;
  const liveMessage =
    !selectionDisabled && showBulkActions
      ? selectAll
        ? _t('All rows selected')
        : _t('{count} {count, plural, one {row} other {rows}} selected', { count: selectedCount })
      : undefined;

  return (
    <div css={styles.container} data-testid="action-table">
      {/* role="status" implies aria-live="polite" + aria-atomic. Always in DOM so
          screen readers register the region before content appears. */}
      <div css={styles.srOnly} role="status" data-testid="action-table-selection-status">
        {liveMessage}
      </div>
      {showBulkActions && (
        <div css={styles.bulkActionsLayer} data-testid="action-table-bulk-actions">
          {bulkActions}
        </div>
      )}
      <div css={styles.tableLayer}>
        <Table<InternalRow<TRow>, InternalKey<TRow>> {...tableProps} columns={allColumns} rows={internalRows} />
      </div>
    </div>
  );
}

export { ActionTable };
